
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Profile } from "@/types/profile";
import AuthContext from "./AuthContext";
import { 
  fetchUserProfile, 
  formatProfileData 
} from "@/utils/authUtils";
import { applyUiSkin, resolveUiSkin } from "@/lib/uiSkin";
import { setupDatabase } from "@/integrations/supabase/setup";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const dbSetupDone = useRef(false);
  const lastSeenWrittenAt = useRef(0);

  // Ops-portal activity heartbeat: stamp profiles.last_seen_at at most once per
  // 5 minutes while signed in. Fire-and-forget — never surfaces to the user.
  const touchLastSeen = (userId: string) => {
    const now = Date.now();
    if (now - lastSeenWrittenAt.current < 5 * 60 * 1000) return;
    lastSeenWrittenAt.current = now;
    void supabase
      .from("profiles")
      .update({ last_seen_at: new Date().toISOString() })
      .eq("id", userId)
      .then(({ error }) => {
        if (error) console.error("last_seen_at update failed:", error);
      });
  };

  const runDatabaseSetupOnce = () => {
    if (dbSetupDone.current) return;
    dbSetupDone.current = true;
    setupDatabase().catch((err) => {
      console.error("Database setup error:", err);
    });
  };

  const checkAuth = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setProfile(null);
        setIsAuthenticated(false);
        return;
      }

      // Check if user's profile exists and isn't deleted
      const profileData = await fetchUserProfile(user.id);

      if (!profileData) {
        setProfile(null);
        setIsAuthenticated(false);
        return;
      }

      // If account is deleted, sign out and prevent access
      if (profileData.is_deleted) {
        await supabase.auth.signOut();
        toast({
          variant: "destructive",
          title: "Account Deleted",
          description: "This account has been deleted and cannot be accessed.",
        });
        setProfile(null);
        setIsAuthenticated(false);
        navigate('/login');
        return;
      }

      // Format the profile data
      const formattedProfile = formatProfileData(profileData);
      
      setProfile(formattedProfile);
      setIsAuthenticated(true);
      runDatabaseSetupOnce();
    } catch (error: any) {
      console.error('Auth check error:', error);
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: error.message,
      });
      setProfile(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setProfile(null);
      setIsAuthenticated(false);
      navigate('/login');
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error signing out",
        description: error.message,
      });
    }
  };

  const refreshProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setProfile(null);
        setIsAuthenticated(false);
        return;
      }

      // Fetch and process profile data
      const profileData = await fetchUserProfile(user.id);

      if (!profileData) {
        setProfile(null);
        setIsAuthenticated(false);
        return;
      }

      // If account is deleted, sign out and prevent access
      if (profileData.is_deleted) {
        await supabase.auth.signOut();
        toast({
          variant: "destructive",
          title: "Account Deleted",
          description: "This account has been deleted and cannot be accessed.",
        });
        setProfile(null);
        setIsAuthenticated(false);
        navigate('/login');
        return;
      }

      // Format the profile data
      const formattedProfile = formatProfileData(profileData);
      
      setProfile(formattedProfile);
      setIsAuthenticated(true);
      runDatabaseSetupOnce();
    } catch (error: any) {
      console.error('Auth check error:', error);
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: error.message,
      });
      setProfile(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        void checkAuth();
      } else {
        setProfile(null);
        setIsAuthenticated(false);
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    applyUiSkin(resolveUiSkin(profile?.id));
  }, [profile?.id]);

  useEffect(() => {
    if (!isAuthenticated || !profile?.id) return;
    touchLastSeen(profile.id);
    const interval = window.setInterval(() => touchLastSeen(profile.id), 5 * 60 * 1000);
    const onFocus = () => touchLastSeen(profile.id);
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAuthenticated, profile?.id]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, profile, loading, checkAuth, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
